<script setup lang="ts">
import { computed } from 'vue'
import { UiText } from '@shopexperts/shared'

interface Props {
  title?: string
  description?: string
  disabled?: boolean
  /** If true, shows slot content with slide-down animation when selected */
  expandable?: boolean
  /** Allow clicking again to deselect */
  allowDeselect?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  expandable: true,
  allowDeselect: true,
})

const model = defineModel<boolean>({ default: false })

const isSelected = computed(() => Boolean(model.value))

function onToggle() {
  if (props.disabled) return
  if (isSelected.value && !props.allowDeselect) return
  model.value = !isSelected.value
}
</script>

<template>
  <div class="flex flex-col">
    <button
      type="button"
      class="border flex flex-col gap-2 rounded-md p-4 cursor-pointer text-left transition-colors hover:bg-se-gray-50"
      :disabled="props.disabled"
      :class="[
        isSelected ? 'bg-se-gray-100 border-se-dark' : 'border-se-gray-200',
        isSelected && props.expandable ? 'rounded-b-none' : '',
        props.disabled ? 'opacity-60 cursor-not-allowed' : '',
      ]"
      @click="onToggle"
    >
      <slot name="header">
        <UiText
          v-if="props.title"
          as="span"
          variant="p"
          class="text-se-dark font-semibold"
          :text="props.title"
        />
        <UiText
          v-if="props.description"
          as="span"
          variant="p-small"
          :text="props.description"
        />
      </slot>
    </button>

    <Transition
      enter-active-class="transition-all duration-300 ease-out overflow-hidden"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-[800px] opacity-100"
      leave-active-class="transition-all duration-200 ease-in overflow-hidden"
      leave-from-class="max-h-[800px] opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div
        v-if="props.expandable && isSelected"
        class="border border-t-0 border-se-dark rounded-b-md bg-se-gray-50 p-4"
      >
        <slot />
      </div>
    </Transition>
  </div>
</template>


